import React, { useEffect } from "react";
import { Sparkles, X } from "lucide-react";
import { useCart } from "../context/CartContext";

/**
 * Smart pack-size tip toast.
 *
 * Activated whenever `addItem` produces a cart line whose quantity is NOT
 * a whole multiple of `pack_size`. Suggests bumping to the next VE
 * multiple so the buyer avoids the €24.50 split-pack surcharge.
 *
 * Auto-dismisses after 8 seconds; manual dismiss via the × button. Only
 * one tip visible at a time — newer tips replace older ones in the
 * provider state.
 */
const AUTO_DISMISS_MS = 8000;

export default function PackTipToast() {
  const { packTip, dismissPackTip, acceptPackTip } = useCart();

  useEffect(() => {
    if (!packTip) return undefined;
    const t = window.setTimeout(dismissPackTip, AUTO_DISMISS_MS);
    return () => window.clearTimeout(t);
  }, [packTip, dismissPackTip]);

  if (!packTip) return null;

  const { fromQty, target, packSize, unitPrice, subpackFee, sku } = packTip;
  const currentLineCost = fromQty * unitPrice + (subpackFee > 0 ? subpackFee : 0);
  const targetLineCost = target * unitPrice;
  const saving = currentLineCost - targetLineCost;
  const isSubpack = fromQty < packSize;
  const showsSaving = saving > 0; // bump-up usually saves €24.50 minus extra units cost

  return (
    <div
      className="fixed bottom-24 right-4 z-50 max-w-sm animate-in slide-in-from-bottom-4 fade-in duration-200"
      data-testid="pack-tip-toast"
      role="status"
      aria-live="polite"
    >
      <div className="overflow-hidden rounded-lg border-2 border-nibro-sage bg-white shadow-2xl shadow-zinc-900/20">
        <div className="flex items-start gap-3 p-3">
          <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
            <Sparkles size={16} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-display text-sm font-bold text-zinc-900 leading-tight">
              Tip: per {packSize} = €{unitPrice.toFixed(2)}/stuk · zonder toeslag
            </div>
            <div className="mt-1 font-mono text-[11px] leading-snug text-zinc-600">
              {isSubpack
                ? `${sku} · ${fromQty} stuks = sub-pack +€${subpackFee.toFixed(2)}.`
                : `${sku} · ${fromQty} stuks = ${Math.floor(fromQty / packSize)}× verpakking + ${fromQty % packSize} los — +€${subpackFee.toFixed(2)}.`}
              {showsSaving && (
                <span className="ml-1 font-bold text-emerald-700">
                  Bespaart €{saving.toFixed(2)}.
                </span>
              )}
            </div>
            <div className="mt-2.5 flex flex-wrap gap-1.5">
              <button
                type="button"
                onClick={acceptPackTip}
                data-testid="pack-tip-accept"
                className="inline-flex items-center gap-1 rounded-md bg-nibro-sage px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-white transition hover:bg-nibro-sage-hover"
              >
                Maak er {target} van
              </button>
              <button
                type="button"
                onClick={dismissPackTip}
                data-testid="pack-tip-keep"
                className="inline-flex items-center gap-1 rounded-md border border-zinc-200 bg-white px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-zinc-600 transition hover:bg-zinc-50"
              >
                Behoud {fromQty}
              </button>
            </div>
          </div>
          <button
            type="button"
            onClick={dismissPackTip}
            aria-label="Sluiten"
            data-testid="pack-tip-dismiss"
            className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded text-zinc-400 transition hover:bg-zinc-100 hover:text-zinc-700"
          >
            <X size={14} />
          </button>
        </div>
        <div
          className="h-0.5 bg-nibro-sage/30"
          style={{
            animation: `pack-tip-progress ${AUTO_DISMISS_MS}ms linear forwards`,
          }}
        />
      </div>
      <style>{`
        @keyframes pack-tip-progress {
          from { transform: scaleX(1); transform-origin: left; }
          to   { transform: scaleX(0); transform-origin: left; }
        }
      `}</style>
    </div>
  );
}
